// d:\pos_web\src\pages\inventory\InventoryForm.js
import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import InputField from '../../components/InputField';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';

const InventoryForm = ({
  'inventory item': initialData, // Prop name from GenericListPage (singular of "Inventory Items")
  onClose,
  onSaveSuccess
}) => {
  const api = useApi();
  const [formData, setFormData] = useState({
    productId: '',
    quantity: '',
    // storeId: '', // Optional: if you have multi-store inventory
    remarks: '',
  });
  const [products, setProducts] = useState([]);
  // const [stores, setStores] = useState([]); // Optional
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false); // For form submission
  const [pageLoading, setPageLoading] = useState(false); // For fetching dropdown data
  const [error, setError] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setPageLoading(true);
      try {
        const productsResponse = await api.get('products', { queryParams: { limit: 1000, fields: 'name,_id' } });
        setProducts(productsResponse.data || productsResponse || []);
        // Optional: Fetch stores if you have multi-store inventory
        // const storesResponse = await api.get('stores', { queryParams: { limit: 1000, fields: 'name,_id' } });
        // setStores(storesResponse.data || storesResponse || []);
        setError(null);
      } catch (err) {
        console.error("Failed to fetch data for form:", err);
        setError("Failed to load data (products/stores). " + (err.message || ""));
      } finally {
        setPageLoading(false);
      }
    };
    fetchData();
  }, [api]);

  useEffect(() => {
    if (initialData) {
      setIsEditing(true);
      setFormData({
        productId: initialData.productId?._id || initialData.productId || '',
        quantity: initialData.quantity?.toString() || '',
        // storeId: initialData.storeId?._id || initialData.storeId || '', // Optional
        remarks: initialData.remarks || '',
      });
    } else {
      setIsEditing(false);
      setFormData({ productId: '', quantity: '', remarks: '' /* storeId: '' */ });
    }
    setFormErrors({});
  }, [initialData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.productId) errors.productId = 'Product is required.';
    if (!formData.quantity.toString().trim()) errors.quantity = 'Quantity is required.';
    else if (isNaN(parseInt(formData.quantity))) errors.quantity = 'Quantity must be a number.';
    // else if (parseInt(formData.quantity) <= 0) errors.quantity = 'Quantity must be positive.'; // Allow negative for adjustments out
    // if (!formData.storeId) errors.storeId = 'Store is required.'; // Optional
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    const payload = {
      ...formData,
      quantity: parseInt(formData.quantity),
    };

    try {
      if (isEditing) {
        // Assuming an endpoint like 'inventory-items/:id' for individual inventory records
        await api.put(`inventory-items/${initialData._id}`, payload);
        alert("Inventory item updated successfully!");
      } else {
        await api.post('inventory-items', payload);
        alert("Inventory item added successfully!");
      }
      onSaveSuccess();
      onClose();
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Operation failed.");
    } finally {
      setLoading(false);
    }
  };

  if (pageLoading) return <LoadingSpinner />;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {loading && <LoadingSpinner />}
      {error && <ErrorMessage message={error} />}

      <div>
        <label htmlFor="productId" className="block text-sm font-medium text-gray-700">Product</label>
        <select id="productId" name="productId" value={formData.productId} onChange={handleChange} required
          className={`mt-1 block w-full px-3 py-2 border ${formErrors.productId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}>
          <option value="">Select a Product</option>
          {products.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
        </select>
        {formErrors.productId && <p className="mt-1 text-xs text-red-600">{formErrors.productId}</p>}
      </div>

      <InputField label="Quantity" id="quantity" name="quantity" type="number" value={formData.quantity} onChange={handleChange} error={formErrors.quantity} required />
      
      {/* Optional: Store Selector
      <div>
        <label htmlFor="storeId" className="block text-sm font-medium text-gray-700">Store</label>
        <select id="storeId" name="storeId" value={formData.storeId} onChange={handleChange} required
          className={`mt-1 block w-full px-3 py-2 border ${formErrors.storeId ? 'border-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm`}>
          <option value="">Select a Store</option>
          {stores.map(s => <option key={s._id} value={s._id}>{s.name}</option>)}
        </select>
        {formErrors.storeId && <p className="mt-1 text-xs text-red-600">{formErrors.storeId}</p>}
      </div>
      */}

      <InputField label="Remarks (Optional)" id="remarks" name="remarks" type="text" value={formData.remarks} onChange={handleChange} />

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <Button type="button" onClick={onClose} variant="secondary" disabled={loading}>Cancel</Button>
        <Button type="submit" variant="primary" isLoading={loading} disabled={loading}>
          {isEditing ? 'Save Changes' : 'Add Inventory Item'}
        </Button>
      </div>
    </form>
  );
};

export default InventoryForm;